import type { Beneficiary, BeneficiaryDistribution, InvestorProfile, Structure } from "../types/entities";

/**
 * B132 (Day 55) — Beneficiary income / Medicare-exempt SSOT resolver.
 *
 * When a `Beneficiary.householdMemberId` is set (set automatically by
 * `addBeneficiaryFromProfile` per B119 sibling UX), the beneficiary IS a
 * profile entity (primary user or a `HouseholdMember`). The profile already
 * carries the authoritative `annualPreTaxIncome` and `medicareExempt`
 * fields for that person. Storing copies on the beneficiary row creates two
 * sources of truth that drift — Day 55 prod check found 4 of 7
 * beneficiaries on Angus's account stale ($170k stored vs $162k profile).
 *
 * These helpers route every read through the profile when the link is
 * present; manual-add beneficiaries (no `householdMemberId`) keep their
 * inline `annualIncome` / `medicareExempt` because they aren't profile
 * entities.
 *
 * Resolution order (income, mirrored for medicareExempt):
 *   1. `householdMemberId === "primary"` → `profile.annualPreTaxIncome`
 *   2. `householdMemberId` matches a member → that member's
 *      `annualPreTaxIncome`
 *   3. `householdMemberId` set but no match (deleted member) → inline
 *      `annualIncome` (defensive fallback)
 *   4. no `householdMemberId` (manual-add path) → inline `annualIncome`
 *   5. nothing → 0 (or `false` for medicareExempt)
 *
 * Mirrors the `resolveLensProfile` pattern from B103 Phase 2
 * (`src/lib/householdScope.ts`).
 *
 * VISION violations closed: #2 (no data silos), #3 (no duplicate inputs),
 * L48 corollary (tax-input data is SSOT material).
 *
 * Cross-refs: B103 Phase 1 (`householdMemberId` link), B119 sibling UX
 * (`addBeneficiaryFromProfile` writes the link), B131 (Stage 3 brackets
 * applied to wrong income would still be wrong — B132 closes that loop).
 */
export function resolveBeneficiaryIncome(
  beneficiary: Beneficiary,
  profile: InvestorProfile,
): number {
  if (beneficiary.householdMemberId === "primary") {
    return profile.annualPreTaxIncome ?? beneficiary.annualIncome ?? 0;
  }
  if (beneficiary.householdMemberId) {
    const member = profile.householdMembers?.find(
      (m) => m.id === beneficiary.householdMemberId,
    );
    if (member) {
      return member.annualPreTaxIncome ?? beneficiary.annualIncome ?? 0;
    }
    // Defensive: link points to a deleted/missing member; fall back to inline
    return beneficiary.annualIncome ?? 0;
  }
  return beneficiary.annualIncome ?? 0;
}

/**
 * Same resolution semantics for the `medicareExempt` flag.
 *
 * Note: `medicareReduction`, `hasPrivateHospitalCover`, `medicareLevy`,
 * `hasHECS` are NOT on the `Beneficiary` type — only `HouseholdMember` /
 * `InvestorProfile` carry them. Consumers needing those flags must read
 * from profile directly.
 */
export function resolveBeneficiaryMedicareExempt(
  beneficiary: Beneficiary,
  profile: InvestorProfile,
): boolean {
  if (beneficiary.householdMemberId === "primary") {
    return profile.medicareExempt ?? beneficiary.medicareExempt ?? false;
  }
  if (beneficiary.householdMemberId) {
    const member = profile.householdMembers?.find(
      (m) => m.id === beneficiary.householdMemberId,
    );
    if (member) {
      return member.medicareExempt ?? beneficiary.medicareExempt ?? false;
    }
    return beneficiary.medicareExempt ?? false;
  }
  return beneficiary.medicareExempt ?? false;
}

/**
 * Convenience: returns true when the beneficiary is linked to a
 * household member (primary or a `HouseholdMember.id`). Drives UI
 * lockout in `StructureModal` — linked rows show profile-derived
 * income / Medicare read-only; manual-add rows keep editable inputs.
 */
export function isBeneficiaryLinked(beneficiary: Beneficiary): boolean {
  return Boolean(beneficiary.householdMemberId);
}

/**
 * B121 — Resolved distribution for a beneficiary in a given FY.
 *
 * `percent` and `amount` are derived together — percent is the share of
 * trust net income (0-100); amount is the $ allocated. `actualDistributed`
 * is the post-FY truth-up if known.
 *
 * `source` flags the resolution path so the AI / tests can reason about
 * inferred vs explicit allocations.
 */
export type ResolvedDistribution = {
  percent: number;
  amount: number;
  actualDistributed?: number;
  source:
    | "fy-entry"                // explicit distributions[] entry for the requested FY
    | "prior-fy-carryforward"   // no entry for requested FY; using most recent prior FY (s.97 trustee continuity assumption)
    | "legacy-percent"          // fallback to deprecated distributionPercent field
    | "default-equal"           // no data — equal split across beneficiaries (1/N)
    | "smsf-statutory"          // SMSF member entitlement, not discretionary
    | "loss-quarantined"        // trustNetIncome < 0; allocation = 0 per B120
    | "carry-forward-absorbed"; // gross > 0 but adjusted = 0 after B125 carry-forward applied
};

/**
 * B121 — SSOT predicate for beneficiary distribution allocation.
 *
 * Resolution order:
 *   1. Trust loss (trustNetIncome < 0) → all amounts 0 per B120
 *   2. SMSF structure → statutory entitlement; skip per-FY shape
 *   3. distributions[] entry for requested FY → use it
 *   4. Empty distributions[] + legacy distributionPercent set → fallback
 *   5. Empty + no legacy → default-equal split (1/N over allBeneficiaries)
 *
 * Type === "amount" beneficiaries take fixed $; "percent" siblings split
 * the residual proportionally. Sum > 100% logs but doesn't normalise —
 * caller is responsible for surfacing the warning (StructureModal does).
 *
 * Authority: ITAA 1936 s.97 (beneficiary presently entitled); s.99A
 * (undistributed → trustee at 47%, out of scope V1).
 */
export function resolveBeneficiaryDistribution(
  beneficiary: Beneficiary,
  fy: string,
  trustNetIncome: number,
  allBeneficiaries: Beneficiary[],
  structureType?: Structure["entityType"],
): ResolvedDistribution {
  // Layer 1 — trust loss takes precedence over everything (B120).
  if (trustNetIncome < 0) {
    return { percent: 0, amount: 0, actualDistributed: 0, source: "loss-quarantined" };
  }

  // Layer 2 — SMSF: statutory member entitlement, not discretionary.
  if (structureType === "SMSF") {
    return {
      percent: 100,
      amount: trustNetIncome,
      actualDistributed: undefined,
      source: "smsf-statutory",
    };
  }

  // Layer 3 — distributions[] entry for this exact FY.
  const fyEntry = beneficiary.distributions?.find((d) => d.fy === fy);
  if (fyEntry) {
    const percent = derivePercentFromEntry(fyEntry, trustNetIncome);
    return {
      percent,
      amount: trustNetIncome * (percent / 100),
      actualDistributed: fyEntry.actualDistributed,
      source: "fy-entry",
    };
  }

  // Layer 3.5 — most-recent-prior-FY entry (B121 Day 77 fix).
  // Trustee discretion is per-FY (s.97), but trustees typically continue the
  // prior year's ratio unless explicitly changed. If the requested FY has no
  // entry, use the most recent prior FY entry as a "carry-forward default"
  // before falling back to the deprecated legacy field. Source flagged so AI
  // and tests can reason about the inferred default vs explicit current-FY.
  const priorEntry = findMostRecentPriorFY(beneficiary.distributions, fy);
  if (priorEntry) {
    const percent = derivePercentFromEntry(priorEntry, trustNetIncome);
    return {
      percent,
      amount: trustNetIncome * (percent / 100),
      actualDistributed: undefined, // prior-FY actualDistributed doesn't apply to current-FY allocation
      source: "prior-fy-carryforward",
    };
  }

  // Layer 4 — legacy distributionPercent fallback (deprecated).
  if (beneficiary.distributionPercent !== undefined) {
    const percent = beneficiary.distributionPercent;
    return {
      percent,
      amount: trustNetIncome * (percent / 100),
      actualDistributed: undefined,
      source: "legacy-percent",
    };
  }

  // Layer 5 — default-equal split.
  const n = allBeneficiaries.length || 1;
  const percent = 100 / n;
  return {
    percent,
    amount: trustNetIncome * (percent / 100),
    actualDistributed: undefined,
    source: "default-equal",
  };
}

/**
 * Derive percent from a distributions[] entry, accounting for type=amount
 * siblings. Type=amount beneficiaries get fixed $; type=percent siblings
 * split the residual proportionally.
 */
function derivePercentFromEntry(
  entry: BeneficiaryDistribution,
  trustNetIncome: number,
): number {
  if (entry.type === "percent") return entry.value;

  // type === "amount". Convert to percent of trust net income.
  if (trustNetIncome <= 0) return 0;
  return Math.min(100, (entry.value / trustNetIncome) * 100);
}

/**
 * Find the most-recent-prior-FY distribution entry. Returns the entry whose
 * fy field is lexicographically less than the requested fy and is the
 * highest such entry. FY labels in YYYY-YY format sort correctly via string
 * compare ("2024-25" < "2025-26").
 */
function findMostRecentPriorFY(
  distributions: BeneficiaryDistribution[] | undefined,
  fy: string,
): BeneficiaryDistribution | undefined {
  if (!distributions || distributions.length === 0) return undefined;
  const priors = distributions.filter((d) => d.fy < fy);
  if (priors.length === 0) return undefined;
  return priors.reduce((best, d) => (d.fy > best.fy ? d : best));
}
